import { FCM_TOKENS } from '@/config/api/api-routes';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { http } from '@/shared/api/http';
import { getDeviceId } from '@/shared/helpers/get-device-id';
import type { Unsubscribe } from 'firebase/messaging';
import { deleteToken, getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';

interface RegisterFcmTokenPayload {
  token: string;
  deviceId: string;
  deviceType: string;
  deviceName: string;
  platformVersion: string;
  appVersion: string;
}

// Firebase app is initialized in src/config/firebase-config.ts

export async function getFcmToken(vapidKey?: string): Promise<string | undefined> {
  if (!(await isSupported())) return undefined;

  try {
    const messaging = getMessaging();
    const token = await getToken(messaging, { vapidKey });
    return token ?? undefined;
  } catch (error) {
    console.error('[FCM] getToken failed', error);
    return undefined;
  }
}

export async function deleteFcmToken(): Promise<void> {
  if (!(await isSupported())) return;

  try {
    const messaging = getMessaging();

    if (!(await deleteToken(messaging))) throw new Error('Failed to delete token');
  } catch (error) {
    console.error('[FCM] deleteToken failed', error);
    throw error;
  }
}

export function subscribeForegroundMessages(handler: (payload: unknown) => void): Unsubscribe | undefined {
  try {
    const messaging = getMessaging();
    return onMessage(messaging, (payload) => handler(payload));
  } catch (error) {
    console.error('[FCM] subscribeForegroundMessages failed', error);
    return undefined;
  }
}

export function collectDeviceInfo(): Omit<RegisterFcmTokenPayload, 'token'> {
  const deviceId = getDeviceId();
  const deviceType = 'web';
  const deviceName = navigator.userAgent;
  const platformVersion = (navigator as any).userAgentData?.platform ?? navigator.platform;
  const appVersion = import.meta.env.VITE_APP_VERSION ?? 'web';

  return { deviceId, deviceType, deviceName, platformVersion, appVersion };
}

export async function registerTokenToBackend(token: string): Promise<void> {
  const at = await useAuthStore.getState().getValidToken();
  if (!at) return; // Post-login only

  const payload: RegisterFcmTokenPayload = {
    token,
    ...collectDeviceInfo()
  };

  try {
    await http.post(FCM_TOKENS, payload);
    console.info('[FCM] Token registered with backend');
  } catch (error) {
    console.error('[FCM] Failed to register token', error);
  }
}
